import CoreClient from '@c42/core-client';
import { ServerEnv } from '@c42/jwt';
import { LogoutRedirectApp, RATE_LIMITED_ERROR } from '@c42/jwt/dist/constants';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import Text from '../../../../shared/Text';
import c42Jwt from '../../../constants/c42Jwt';
import { MFA_REQUIRED, MFA_SETUP_REQUIRED } from '../../../constants/errors';
import { LOGIN_INDEX, SSO_INDEX } from '../../../constants/routes';
import { performRedirect } from '../../../utils/performRedirect';
import { POST, request, RequestResponse } from '../../../utils/request';
import { useRoutingData } from '../routing/hooks';
import {
  API_TOTP_CREATE_SECRET_KEY_PATH,
  CONSOLE_LOGIN_PERMISSIONS,
  CONSOLE_LOGIN_PERMISSIONS_ERROR,
} from './constants';
import {
  AuthData,
  AuthDataContext,
  AuthenticateOptions,
  DEFAULT_AUTH_DATA,
  STEP_TOTP_CONFIG,
  STEP_TOTP_SN_WARNING,
  STEP_USERNAME,
} from './context';

const AuthDataProvider: React.FC<React.PropsWithChildren<Record<never, unknown>>> = ({ children }) => {
  const [authData, setAuthData] = React.useState<AuthData>(DEFAULT_AUTH_DATA);

  const history = useHistory();
  const location = useLocation();
  const { redirectParam } = useRoutingData();

  React.useEffect(() => {
    if (location?.pathname !== SSO_INDEX) {
      setAuthData((current) => ({
        ...current,
        loginError: null,
        password: null,
        totp: null,
        totpSecret: null,
        username: null,
      }));
    }
  }, [location]);

  const authenticate = async (opts?: AuthenticateOptions) => {
    setAuthData((current) => ({
      ...current,
      isAuthenticating: true,
      loginError: null,
    }));

    try {
      if (!opts) {
        await c42Jwt.verifyAuthenticated();
      } else if (opts.loginToken) {
        // If there's already an OAuth token in localstorage, clear it and login again.
        await c42Jwt
          .verifyAuthenticated()
          .then(() => c42Jwt.logout())
          .catch(() => Promise.resolve()) // Swallow expected exception when we are unauthenticated
          .then(() => c42Jwt.loginWithLoginToken(opts.loginToken));
      } else if (opts.isLogin && opts.username && opts.password) {
        await c42Jwt.login(opts.username, opts.password, opts.totp || '');
      } else {
        throw 'Unsupported scenario encountered, opts: ' + JSON.stringify(opts);
      }
    } catch (e) {
      setAuthData((current) => ({
        ...current,
        isAuthenticating: false,
        loginError: null,
      }));
      throw e;
    }
  };

  const getServerEnv = async () => {
    const serverEnv: ServerEnv = await CoreClient.ServerEnv.find();
    c42Jwt.register(serverEnv);
    const { serverType } = serverEnv;
    setAuthData((current) => ({
      ...current,
      serverType,
    }));
    return serverEnv;
  };

  const resetForm = () => {
    history.push(LOGIN_INDEX);
    setAuthData((current) => ({
      ...current,
      currentStep: STEP_USERNAME,
    }));
  };

  const fetchTotpSecret = async () => {
    setAuthData((current) => ({
      ...current,
      isAuthenticating: true,
    }));

    return request(API_TOTP_CREATE_SECRET_KEY_PATH, POST, {
      body: JSON.stringify({
        username: authData.username,
        password: authData.password,
      }),
      headers: {
        'content-type': 'application/json',
      },
    })
      .then((response: RequestResponse<{ data: { secretKey: string } }>) => {
        const { body } = response;
        if (!body?.data?.secretKey) {
          return Promise.reject('NO_SECRET_KEY');
        }
        setAuthData((current) => ({
          ...current,
          isAuthenticating: false,
          totpSecret: body.data.secretKey,
        }));
      })
      .catch(() => {
        setAuthData((current) => ({
          ...current,
          isAuthenticating: false,
          loginError: Text.get('login_error_incorrect'),
        }));
      });
  };

  const submitLogin = async () => {
    return authenticate({
      isLogin: true,
      username: authData.username,
      password: authData.password,
      totp: authData.totp,
    })
      .then(() => {
        const permissions = c42Jwt.extractPermissionsFromJwt();
        if (permissions?.length > 0) {
          const hasPermission = Boolean(
            permissions.find((permission) => CONSOLE_LOGIN_PERMISSIONS.indexOf(permission) >= 0),
          );
          if (hasPermission) {
            return true;
          }
        }
        throw CONSOLE_LOGIN_PERMISSIONS_ERROR;
      })
      .then(
        () => {
          c42Jwt.setLogoutRedirectApp(authData.logoutRedirectApp ?? LogoutRedirectApp.INCYDR);
          performRedirect(redirectParam);
        },
        async (err: JQueryXHR) => {
          c42Jwt.logout();

          // ServerEnv is reloaded on auth error to reload challenge from server.
          const serverEnv = await getServerEnv();
          const isStorageNode = serverEnv?.serverType?.storage;
          switch (err.toString()) {
            // PL-116991 emits MFA_SETUP_REQUIRED; legacy key to be removed in future PRM-5107 ticket
            case MFA_SETUP_REQUIRED:
              if (isStorageNode) {
                setAuthData((current) => ({
                  ...current,
                  currentStep: STEP_TOTP_SN_WARNING,
                }));
              } else {
                await fetchTotpSecret();
                setAuthData((current) => ({
                  ...current,
                  currentStep: STEP_TOTP_CONFIG,
                }));
              }
              return;

            case MFA_REQUIRED:
              setAuthData((current) => ({
                ...current,
                isUsingTotp: true,
              }));
              return;

            case CONSOLE_LOGIN_PERMISSIONS_ERROR:
              setAuthData((current) => ({
                ...current,
                isAuthenticating: false,
                loginError: Text.get('login_error_auth'),
              }));
              return;

            case RATE_LIMITED_ERROR:
              setAuthData((current) => ({
                ...current,
                isAuthenticating: false,
                loginError: Text.get('login_error_rate_limited'),
              }));
              return;

            default:
              setAuthData((current) => ({
                ...current,
                isAuthenticating: false,
                loginError: Text.get('login_error_incorrect'),
              }));
              return;
          }
        },
      );
  };

  return (
    <AuthDataContext.Provider
      value={{ authData, authenticate, getServerEnv, resetForm, setAuthData, submitLogin }}
    >
      {children}
    </AuthDataContext.Provider>
  );
};

export default AuthDataProvider;
